// Reusable "where did I come from" context for dashboard-driven navigation.
//
// Dashboards push a small, typed marker onto the router history state when they
// send the user to an operational screen. Destination screens read it (via
// <DashboardOriginBar />) to offer a contextual return action and to show which
// site/region scope the user was working in. It is presentation context only —
// it never affects permissions, RLS, or which records a screen may load.
import { useLocation } from "@tanstack/react-router";
import type { HistoryState } from "@tanstack/react-router";
import { NAV_CATALOG } from "@/lib/nav-catalog";
import { previousPath } from "@/lib/nav-trail";

export type DashboardOriginId = "supervisor" | "manager" | "owner" | "operations";

export type DashboardOrigin = {
  id: DashboardOriginId;
  /** Selected site at the time of navigation ("all" for every authorized site). */
  siteId?: string;
  /** Human label for the selected scope, e.g. "All sites" or "North Tower". */
  scopeLabel?: string;
};

const LABELS: Record<DashboardOriginId, { label: string; to: string }> = {
  supervisor: { label: "Supervisor Dashboard", to: "/supervisor" },
  manager: { label: "Manager Dashboard", to: "/manager" },
  owner: { label: "Dashboard", to: "/dashboard" },
  operations: { label: "Operations", to: "/operations" },
};

export function dashboardOriginLabel(id: DashboardOriginId) {
  return LABELS[id].label;
}

export function dashboardOriginPath(id: DashboardOriginId) {
  return LABELS[id].to;
}

/** Build the history state to attach to a `Link`/`navigate` from a dashboard. */
export function fromDashboard(origin: DashboardOrigin): HistoryState {
  return { dashboardOrigin: origin } as HistoryState;
}

/** Read the dashboard origin (if any) for the current location. */
export function useDashboardOrigin(): DashboardOrigin | null {
  const location = useLocation();
  const raw = (location.state as { dashboardOrigin?: DashboardOrigin } | undefined)
    ?.dashboardOrigin;
  if (!raw || !(raw.id in LABELS)) return null;
  return raw;
}

/** Parent contexts that always warrant a contextual return action. */
const PARENT_PATHS: { path: string; label: string }[] = [
  { path: "/supervisor", label: "Supervisor Dashboard" },
  { path: "/manager", label: "Manager Dashboard" },
  { path: "/dashboards", label: "Dashboard" },
  { path: "/operations/live", label: "Live Ops" },
  { path: "/operations", label: "Operations Center" },
  { path: "/home", label: "Home" },
];

function catalogLabel(path: string): string | null {
  const match = NAV_CATALOG.filter(
    (i) => path === i.route || path.startsWith(`${i.route}/`),
  ).sort((a, b) => b.route.length - a.route.length)[0];
  return match?.label ?? null;
}

/** Label for a known parent path, or null when it isn't a recognised screen. */
export function parentLabelFor(path: string): string | null {
  const parent = PARENT_PATHS.find((p) => path === p.path);
  if (parent) return parent.label;
  return catalogLabel(path);
}

export type BackContext = { label: string; to: string; scopeLabel?: string };

/**
 * Resolve the contextual return action for the current screen:
 * 1. an explicit dashboard-origin marker attached by the originating link, else
 * 2. the previously visited screen when it is a dashboard/operations parent or
 *    the direct parent of the current path (e.g. /reports -> /reports/new).
 */
export function useBackContext(): BackContext | null {
  const pathname = useLocation({ select: (l) => l.pathname });
  const explicit = useDashboardOrigin();
  if (explicit) {
    return {
      label: dashboardOriginLabel(explicit.id),
      to: dashboardOriginPath(explicit.id),
      scopeLabel: explicit.scopeLabel,
    };
  }

  // Never offer "back" from a parent screen itself.
  if (PARENT_PATHS.some((p) => p.path === pathname)) return null;

  const prev = previousPath(pathname);
  if (!prev) return null;

  const isParentScreen = PARENT_PATHS.some((p) => p.path === prev);
  const isAncestor = pathname.startsWith(`${prev}/`);
  if (!isParentScreen && !isAncestor) return null;

  const label = parentLabelFor(prev);
  if (!label) return null;
  return { label, to: prev };
}

